import { useId, useRef, useState, type FormEvent } from "react";
import type { ChangeOwnerBody, LeadWriteResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { Select } from "../../design/components/Select";
import { TextArea } from "../../design/components/TextArea";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { leadOwnerOptionsQuery, SupersededWriteError, type CardLead } from "../lead-queries";

// «Cambia owner» (piano M2 Task 18, mock-up `scheda-lead.html`, finestra dell'owner): soltanto con la funzione
// `change_owner` (la riga «Owner» lo sa dal server). Il nuovo owner fra le persone che possono esserlo, oppure «Senza
// owner»; il motivo è obbligatorio e resta nella Timeline. Le attività passate restano a nome di chi le ha fatte.

/** Il valore di «Senza owner» nel selettore (un identificativo non è mai vuoto). */
const NO_OWNER = "";

type Props = {
  tab: TabContext;
  lead: CardLead;
  onSubmit: (body: Omit<ChangeOwnerBody, "version">) => Promise<LeadWriteResponse>;
  onClose: () => void;
};

export function OwnerDialog({ tab, lead, onSubmit, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const formId = useId();
  const owners = useQuery(leadOwnerOptionsQuery(tab));
  const currentId = lead.owner?.membershipId ?? null;
  const options = [
    ...(owners.data ?? []).filter((option) => option.membershipId !== currentId).map((option) => ({ value: option.membershipId, label: option.displayName })),
    // Togliere l'owner è un cambio soltanto se il Lead ne ha uno.
    ...(currentId === null ? [] : [{ value: NO_OWNER, label: t("leadCard.owner.none") }]),
  ];
  const [chosen, setChosen] = useState<string | null>(null);
  const value = chosen ?? options[0]?.value ?? NO_OWNER;
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [ownerError, setOwnerError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending || options.length === 0) return;
    setOwnerError(null);
    setFormError(null);
    if (!reason.trim()) {
      setReasonError(t("leadCard.owner.reasonRequired"));
      reasonRef.current?.focus();
      return;
    }
    setReasonError(null);
    setPending(true);
    const ownerMembershipId = value === NO_OWNER ? null : value;
    try {
      const response = await onSubmit({ ownerMembershipId, reason: reason.trim() });
      onClose();
      const name = response.lead.owner?.displayName;
      toast.show(name ? t("leadCard.owner.changed", { name }) : t("leadCard.owner.removed"), "success");
    } catch (error) {
      setPending(false);
      if (error instanceof ApiRequestError && error.code === "reason_required") {
        setReasonError(t("leadCard.owner.reasonRequired"));
        reasonRef.current?.focus();
      } else if (error instanceof ApiRequestError && error.code === "owner_not_eligible") {
        setOwnerError(messageForError(t, error));
      } else if ((error instanceof ApiRequestError && error.code === "version_conflict") || error instanceof SupersededWriteError) {
        // La scheda è già stata riletta: un nuovo «Cambia owner» parte con la versione nuova.
        setFormError(t("leadCard.conflict"));
      } else {
        setFormError(messageForError(t, error));
      }
    }
  }

  const reasonId = `${formId}-reason`;
  return (
    <Dialog
      open
      title={t("leadCard.owner.title")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" type="submit" form={formId} disabled={pending || options.length === 0} aria-busy={pending ? "true" : undefined}>
            {t("leadCard.owner.submit")}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={(event) => void handleSubmit(event)} aria-busy={pending} noValidate>
        {owners.isError ? <p className="g-field__error" role="alert">{messageForError(t, owners.error)}</p> : null}
        {owners.isSuccess && options.length === 0 ? <p className="g-field__hint">{t("leadCard.owner.noOptions")}</p> : null}
        <Select
          id={`${formId}-owner`}
          label={t("leadCard.owner.select")}
          value={value}
          onChange={setChosen}
          options={options}
          disabled={options.length === 0}
          error={ownerError ?? undefined}
        />
        <TextArea
          ref={reasonRef}
          id={reasonId}
          label={t("leadCard.owner.reason")}
          className="g-lead-owner__reason"
          rows={3}
          maxLength={500}
          value={reason}
          placeholder={t("leadCard.owner.reasonPlaceholder")}
          error={reasonError ?? undefined}
          onChange={(event) => setReason(event.target.value)}
        />
        <p className="g-field__hint">{t("leadCard.owner.history")}</p>
        {formError ? (
          <p className="g-field__error" role="alert">
            {formError}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
